/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import { Suspense, useContext, useMemo, useEffect, useState, useCallback } from "react";
import { setNavigate } from "../services/api/navigator";
import { CircularProgress, Container } from "@mui/material";
import WelcomeSplash from "../components/welcome-splash";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSelector } from "react-redux";

import ErrorBoundary from "../components/error-boundary";
import routes from "./routes";
import useAuthorized from "../hooks/use-authorized.hook";
import { SidebarContext } from "../components/layouts/sidebar/context/sidebar.context";
import { HeaderContext } from "../components/layouts/header/context/header.context";
import RouteEnum from "./enums/route.enum";
import useSubscription from "../hooks/use-subscription.hook";
import { selectAuthUser } from "../store/selectors";
import {
  getInviteTokenFromUrl,
} from "../utils/invitation";
import { AUTH_PATHS } from "../configs/constants";

// Helper to check if current path is an auth path
const isAuthPage = (pathname) => AUTH_PATHS.includes(pathname);

const Router = () => {
  const isAuth = useAuthorized();
  const sidebar = useContext(SidebarContext);
  const header = useContext(HeaderContext);
  const { hasActiveSubscription, isLoading: isLoadingSubscription } =
    useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasInviteParams, setHasInviteParams] = useState(false);
  const [isCheckingInvite, setIsCheckingInvite] = useState(true);
  const authUser = useSelector(selectAuthUser);

  const [showSplash, setShowSplash] = useState(
    () => !sessionStorage.getItem("welcome_splash_shown")
  );
  const handleDismissSplash = useCallback(() => {
    sessionStorage.setItem("welcome_splash_shown", "1");
    setShowSplash(false);
  }, []);

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  // Determine if we're on an auth page that should use full-width layout
  const isOnAuthPage = isAuthPage(location.pathname);
  const isCustomersPage = location.pathname === "/customers";
  const isWatchlistPage = location.pathname === "/watchlist";

  const isHeaderVisible = header?.visible !== false;

  // First priority: Check if we're processing an invite
  // This must run before ANY other logic
  useEffect(() => {
    try {
      // Special case for malformed URL: /accept_invite=true&token=xyz
      if (location.pathname.startsWith("/accept_invite=true")) {
        // Extract token from the malformed path
        const pathString = location.pathname;
        const tokenMatch = pathString.match(/token=([^&]+)/);
        const token = tokenMatch ? tokenMatch[1] : null;

        if (token) {
          // Redirect to proper URL format
          navigate(
            `${RouteEnum.ACCEPT_INVITE}?accept_invite=true&token=${token}`,
            { replace: true }
          );
          setHasInviteParams(true);
          setIsCheckingInvite(false);
          return;
        }
      }

      // First, check the standard format from our app
      const params = new URLSearchParams(location.search);
      const acceptInvite = params.get("accept_invite");
      const token = params.get("token");

      // Check for invitation parameters in standard format
      if (acceptInvite === "true" && token) {
        setHasInviteParams(true);

        // Special handling for root path - always redirect to accept-invite
        if (location.pathname === "/") {
          navigate(
            `${RouteEnum.ACCEPT_INVITE}?accept_invite=true&token=${token}`,
            { replace: true }
          );
          return;
        }

        // Only redirect if we're not already on the accept invite page
        if (location.pathname !== RouteEnum.ACCEPT_INVITE) {
          // Use replace to prevent history stacking and back button issues
          navigate(
            `${RouteEnum.ACCEPT_INVITE}?accept_invite=true&token=${token}`,
            { replace: true }
          );
        }
      }
      // Special case: Check if we're on the accept-invite page with just a token
      // This handles direct navigation from email links
      else if (location.pathname === RouteEnum.ACCEPT_INVITE && !acceptInvite) {
        const urlToken = getInviteTokenFromUrl();

        if (urlToken) {
          // Convert to our standard format for consistency
          navigate(
            `${RouteEnum.ACCEPT_INVITE}?accept_invite=true&token=${urlToken}`,
            { replace: true }
          );
          setHasInviteParams(true);
        } else {
          navigate(RouteEnum.LOGIN, { replace: true });
        }
      } else {
        setHasInviteParams(false);
      }

      // Mark invite checking as complete
      setIsCheckingInvite(false);
    } catch (err) {
      setHasInviteParams(false);
      setIsCheckingInvite(false);
    }
  }, [location.pathname, location.search, navigate]);

  // Wait for our hasInviteParams check before proceeding with other routing logic
  const mappedRoutes = useMemo(() => {
    return routes.map((route) => {
      // HIGHEST PRIORITY: If we have an invite link and this is the accept invite route,
      // bypass all other checks and render it directly
      if (route.path === RouteEnum.ACCEPT_INVITE && hasInviteParams) {
        return {
          ...route,
          publicOnly: true,
          authOnly: false,
          requiresSubscription: false,
        }; // Explicitly override all restrictions
      }

      // Only apply auth checks if we're not processing an invite
      if (!hasInviteParams) {
        // Authenticated user whose company is still pending staff approval: lock them
        // to the pending-approval page. They already have a company + admin role, so the
        // onboarding lock below wouldn't catch them.
        if (isAuth && authUser && authUser.requires_approval === true) {
          if (route.path !== RouteEnum.ONBOARDING_PENDING_APPROVAL) {
            return {
              ...route,
              Component: () => (
                <Navigate to={RouteEnum.ONBOARDING_PENDING_APPROVAL} replace />
              ),
            };
          }
          return {
            ...route,
            authOnly: true,
            requiresSubscription: false,
          };
        }

        // Authenticated user without a CompanyUserRole: lock them to the onboarding page
        // until they create their company. Without this, they would bounce around between
        // /upload, /company and /login depending on subscription/role checks.
        if (
          isAuth &&
          authUser &&
          (authUser.requires_onboarding === true || authUser.role === null) &&
          authUser.role !== "auditor" // auditors handled separately below
        ) {
          if (route.path !== RouteEnum.ONBOARDING_CREATE_COMPANY) {
            return {
              ...route,
              Component: () => (
                <Navigate to={RouteEnum.ONBOARDING_CREATE_COMPANY} replace />
              ),
            };
          }
          // For the onboarding route itself, keep it auth-gated but skip the
          // generic auth/subscription checks below so it always renders.
          return {
            ...route,
            authOnly: true,
            requiresSubscription: false,
          };
        }

        // Conversely, a fully-onboarded user should never see the onboarding page.
        if (
          route.path === RouteEnum.ONBOARDING_CREATE_COMPANY &&
          isAuth &&
          authUser &&
          authUser.role &&
          authUser.requires_onboarding !== true
        ) {
          return {
            ...route,
            Component: () => (
              <Navigate to={RouteEnum.UPLOADING_FILES} replace />
            ),
          };
        }

        // Auditor: redirect every route except /audit to /audit
        if (authUser && authUser.role === "auditor") {
          if (route.path !== RouteEnum.AUDIT) {
            return {
              ...route,
              Component: () => <Navigate to={RouteEnum.AUDIT} replace />,
            };
          }
          return route;
        }

        // Check for role-restricted routes (Company)
        if (
          route.path === RouteEnum.COMPANY &&
          authUser &&
          authUser.role === "user" // Only redirect users with user role
        ) {
          return {
            ...route,
            Component: () => (
              <Navigate to={RouteEnum.UPLOADING_FILES} replace />
            ),
          };
        }

        // Handle unauthorized routes
        if (
          (isAuth && !(route.authOnly || !route.publicOnly)) ||
          (!isAuth && route.authOnly)
        ) {
          return {
            ...route,
            Component: () => (
              <Navigate to={route.redirectTo || RouteEnum.LOGIN} replace />
            ),
          };
        }

        // Handle subscription-required routes
        if (
          isAuth &&
          route.authOnly &&
          route.requiresSubscription &&
          !isLoadingSubscription &&
          !hasActiveSubscription &&
          route.path !== RouteEnum.COMPANY &&
          route.path !== RouteEnum.AUDIT
        ) {
          return {
            ...route,
            Component: () => <Navigate to={RouteEnum.COMPANY} />,
          };
        }
      }

      return route;
    });
  }, [
    isAuth,
    hasActiveSubscription,
    isLoadingSubscription,
    hasInviteParams,
    authUser,
  ]);

  // Show loading indicator while checking for invite parameters.
  // isAuth is now always a boolean (never null) so we only gate on invite detection.
  if (isCheckingInvite) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  const contentContainerSx = {
    paddingLeft: "0 !important",
    paddingRight: "0 !important",
    paddingBottom: "0 !important",
    paddingTop: (!isHeaderVisible || isCustomersPage || isWatchlistPage) ? "0 !important" : "58px !important",
    width: isOnAuthPage
      ? "100%"
      : `calc(100% - ${sidebar.opened ? "180px" : "64px"})`,
    marginLeft: isOnAuthPage ? 0 : (sidebar.opened ? "180px" : "64px"),
    transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    overflowX: "hidden",
  };

  return (
    <>
      {isAuth && authUser && !isOnAuthPage && showSplash && (
        <WelcomeSplash onDismiss={handleDismissSplash} />
      )}
      <Container
        sx={contentContainerSx}
        maxWidth={isOnAuthPage ? "xl" : false}
      >
        <ErrorBoundary pathname={location.pathname} username={authUser?.username || authUser?.email || ""}>
          {/* Catches render errors and lazy() chunk load failures; sends email to support@kaimakiweb.com */}
          <Suspense fallback={<CircularProgress />}>
            <Routes>
              {mappedRoutes.map(({ path, Component }, index) => (
                <Route key={index} path={path} Component={Component} />
              ))}
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Container>
    </>
  );
};

export default Router;
